import { useEffect, useRef, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * A panel that slides in from the bottom on phones and from the right on
 * wider screens. Use it for details, filters and forms that would be a
 * modal on desktop: on a phone a sheet keeps the thumb near the actions and
 * the page context visible above it.
 *
 * `side="bottom"` keeps it a bottom sheet at every width (short pickers).
 */
export function Sheet({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  side = 'auto',
  size = 'md',
  dismissible = true,
}: {
  open: boolean
  onClose: () => void
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /** auto: bottom on phones, right drawer from md up. */
  side?: 'auto' | 'bottom' | 'right'
  size?: 'sm' | 'md' | 'lg'
  dismissible?: boolean
}) {
  const panel = useRef<HTMLDivElement>(null)
  // `onClose` is usually inline (new every render): through refs, the effect runs only on
  // open, so typing in the sheet does not pull focus back onto the panel.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const dismissibleRef = useRef(dismissible)
  dismissibleRef.current = dismissible

  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissibleRef.current) onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    panel.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      previous?.focus?.()
    }
  }, [open])

  if (!open) return null

  const bottom = side === 'bottom'
  const right = side === 'right'
  const widths = { sm: 'md:w-[400px]', md: 'md:w-[520px]', lg: 'md:w-[720px]' }[size]

  return (
    <div className="fixed inset-0 z-50" role="presentation">
      <div
        className="absolute inset-0 animate-fade-in bg-ink/40 backdrop-blur-[2px]"
        onClick={dismissible ? onClose : undefined}
      />
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={cn(
          'absolute flex flex-col bg-surface shadow-float outline-none',
          // phone: bottom sheet
          !right && 'inset-x-0 bottom-0 max-h-[92dvh] animate-slide-up rounded-t-[20px]',
          // md+: right drawer unless forced to the bottom
          !bottom && !right && cn('md:inset-y-0 md:left-auto md:right-0 md:max-h-none md:animate-slide-in-right md:rounded-none md:border-l md:border-line', widths),
          right && cn('inset-y-0 right-0 w-full animate-slide-in-right border-l border-line', widths),
          bottom && 'md:mx-auto md:max-w-[560px] md:rounded-t-[20px]',
        )}
      >
        {!right && <span aria-hidden="true" className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-pill bg-line-strong md:hidden" />}
        {(title || dismissible) && (
          <header className="flex items-start justify-between gap-3 px-5 pb-3 pt-4 md:pt-5">
            <div className="min-w-0">
              {title && <h2 className="text-[17px] font-semibold leading-snug tracking-tight text-ink">{title}</h2>}
              {description && <p className="mt-0.5 text-[13px] text-ink-muted">{description}</p>}
            </div>
            {dismissible && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
              >
                <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </header>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">{children}</div>
        {footer && (
          <footer
            className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 pt-3"
            style={{ paddingBottom: 'calc(12px + var(--safe-bottom))' }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}
